/**
 * Search utility functions for parsing and filtering job search queries
 */

export const parseQueryToFilters = (query) => {
  const filters = {
    query: query?.trim() || '',
    location: undefined,
    workMode: 'ANY',
    jobTypes: [],
    datePosted: 'ANY',
    page: 1
  };

  if (!query?.trim()) return filters;

  const lowerQuery = query?.toLowerCase();

  // Parse location patterns
  const locationPatterns = [
    /in\s+([^,]+(?:,\s*[^,]+)*)/gi,
    /at\s+([^,]+(?:,\s*[^,]+)*)/gi,
    /near\s+([^,]+(?:,\s*[^,]+)*)/gi,
  ];

  for (const pattern of locationPatterns) {
    const match = pattern?.exec(lowerQuery);
    if (match) {
      const locationText = match?.[1]?.trim();
      // Simple parsing - in production, you'd use a proper geocoding service
      const parts = locationText?.split(',')?.map(p => p?.trim());
      filters.location = {
        city: parts?.[0],
        region: parts?.[1],
        country: parts?.[2]
      };
      break;
    }
  }

  // Parse work mode
  if (lowerQuery?.includes('remote')) {
    filters.workMode = 'REMOTE';
  } else if (lowerQuery?.includes('hybrid')) {
    filters.workMode = 'HYBRID';
  } else if (lowerQuery?.includes('onsite') || lowerQuery?.includes('on-site')) {
    filters.workMode = 'ONSITE';
  }

  // Parse date posted
  if (lowerQuery?.includes('today') || lowerQuery?.includes('24h')) {
    filters.datePosted = '24H';
  } else if (lowerQuery?.includes('week') || lowerQuery?.includes('7d')) {
    filters.datePosted = '7D';
  } else if (lowerQuery?.includes('month') || lowerQuery?.includes('30d')) {
    filters.datePosted = '30D';
  }

  // Parse job types
  const jobTypeKeywords = {
    'full-time': 'FULL_TIME',
    'fulltime': 'FULL_TIME',
    'full time': 'FULL_TIME',
    'part-time': 'PART_TIME',
    'parttime': 'PART_TIME',
    'part time': 'PART_TIME',
    'contract': 'CONTRACT',
    'contractor': 'CONTRACT',
    'temporary': 'TEMPORARY',
    'temp': 'TEMPORARY',
    'intern': 'INTERN',
    'internship': 'INTERN',
    'volunteer': 'VOLUNTEER'
  };

  for (const [keyword, type] of Object.entries(jobTypeKeywords)) {
    if (lowerQuery?.includes(keyword)) {
      if (!filters?.jobTypes?.includes(type)) {
        filters?.jobTypes?.push(type);
      }
    }
  }

  return filters;
};

export const applyFilters = (searchData, filters) => {
  // Ensure searchData is an array and provide fallback
  const dataArray = Array.isArray(searchData) ? searchData : [];
  
  // Separate data by type - handle mixed array format
  const jobs = dataArray?.filter(item => !item?.type || item?.type === 'job') || [];
  const companies = dataArray?.filter(item => item?.type === 'company') || [];
  const locations = dataArray?.filter(item => item?.type === 'location') || [];
  
  let results = [];

  // Apply filters to jobs
  let filteredJobs = [...jobs];

  // CRITICAL FIX: Enhanced filtering for closed jobs - this must be the first filter applied
  filteredJobs = filteredJobs?.filter(job => {
    // If job has no locations, consider it closed
    if (!job?.locations || job?.locations?.length === 0) {
      return false;
    }
    
    // If job status is explicitly CLOSED, exclude from search (CRITICAL: always check this first)
    if (job?.status === 'CLOSED') {
      return false;
    }
    
    // Check if job has at least one open location
    if (job?.locationStatuses) {
      let hasOpenLocation = false;
      
      for (const location of job?.locations) {
        const locationStatus = job?.locationStatuses?.[location?.locationId];
        
        // If no status exists, assume ACTIVE (default behavior)
        if (!locationStatus) {
          hasOpenLocation = true;
          break;
        }
        
        // Check if location is explicitly CLOSED
        if (locationStatus?.status === 'CLOSED') {
          continue; // Skip this closed location
        }
        
        // Check if validThrough date has passed
        if (locationStatus?.validThrough && new Date(locationStatus?.validThrough) < new Date()) {
          continue; // Skip expired locations
        }
        
        // If we reach here, the location is active
        hasOpenLocation = true;
        break;
      }
      
      // If no open locations found, exclude from search results
      if (!hasOpenLocation) {
        return false;
      }
    }
    
    return true;
  });

  // Query filter for jobs
  if (filters?.query?.trim()) {
    const queryLower = filters?.query?.toLowerCase();
    filteredJobs = filteredJobs?.filter(job =>
      job?.title?.toLowerCase()?.includes(queryLower) ||
      job?.companyName?.toLowerCase()?.includes(queryLower) ||
      job?.descriptionHtml?.toLowerCase()?.includes(queryLower) ||
      job?.tags?.some(tag => tag?.toLowerCase()?.includes(queryLower))
    );
  }

  // Location filter
  if (filters?.location?.city || filters?.location?.region || filters?.location?.country) {
    filteredJobs = filteredJobs?.filter(job => {
      return job?.locations?.some(loc => {
        const cityMatch = !filters?.location?.city || 
          loc?.city?.toLowerCase()?.includes(filters?.location?.city?.toLowerCase());
        const regionMatch = !filters?.location?.region || 
          loc?.region?.toLowerCase()?.includes(filters?.location?.region?.toLowerCase());
        const countryMatch = !filters?.location?.country || 
          loc?.country?.toLowerCase()?.includes(filters?.location?.country?.toLowerCase());
        
        return cityMatch && regionMatch && countryMatch;
      });
    });
  }

  // Work mode filter
  if (filters?.workMode && filters?.workMode !== 'ANY') {
    filteredJobs = filteredJobs?.filter(job => job?.workMode === filters?.workMode);
  }

  // Job types filter
  if (filters?.jobTypes?.length > 0) {
    filteredJobs = filteredJobs?.filter(job => 
      filters?.jobTypes?.includes(job?.employmentType)
    );
  }

  // Date posted filter
  if (filters?.datePosted && filters?.datePosted !== 'ANY') {
    const now = new Date();
    const cutoffDates = {
      '24H': new Date(now - 24 * 60 * 60 * 1000),
      '7D': new Date(now - 7 * 24 * 60 * 60 * 1000),
      '30D': new Date(now - 30 * 24 * 60 * 60 * 1000)
    };

    const cutoff = cutoffDates?.[filters?.datePosted];
    if (cutoff) {
      filteredJobs = filteredJobs?.filter(job => 
        new Date(job?.postedAt) >= cutoff
      );
    }
  }

  // Add job results - FIXED: wrap properly for consistent structure
  results = results?.concat(filteredJobs?.map(job => ({ 
    type: 'job', 
    data: job,
    id: job?.id,
    title: job?.title,
    companyName: job?.companyName,
    locations: job?.locations,
    employmentType: job?.employmentType,
    workMode: job?.workMode,
    salary: job?.salary,
    postedAt: job?.postedAt,
    tags: job?.tags,
    isFeatured: job?.isFeatured
  })));

  // Apply filters to companies - ENHANCED: Only include companies that have active jobs
  let filteredCompanies = [...companies];
  
  if (filters?.query?.trim()) {
    const queryLower = filters?.query?.toLowerCase();
    
    // Get company IDs and names that have matching active jobs
    const matchingJobCompanyIds = new Set(
      filteredJobs?.map(job => job?.companyId || job?.companySlug)?.filter(Boolean)
    );
    
    const matchingJobCompanyNames = new Set(
      filteredJobs?.map(job => job?.companyName)?.filter(Boolean)?.map(name => name?.toLowerCase())
    );

    filteredCompanies = filteredCompanies?.filter(company => {
      // Direct company match
      const directMatch = company?.name?.toLowerCase()?.includes(queryLower) ||
        company?.industry?.toLowerCase()?.includes(queryLower) ||
        company?.summary?.toLowerCase()?.includes(queryLower) ||
        company?.description?.toLowerCase()?.includes(queryLower) ||
        company?.keywords?.some(keyword => keyword?.toLowerCase()?.includes(queryLower));

      // Company has matching active jobs
      const hasActiveMatchingJobs = matchingJobCompanyIds?.has(company?.id) ||
        matchingJobCompanyIds?.has(company?.slug) ||
        matchingJobCompanyNames?.has(company?.name?.toLowerCase());

      return directMatch || hasActiveMatchingJobs;
    });
  } else {
    // CRITICAL FIX: Even without a query, only show companies that have active jobs
    const activeJobCompanyIds = new Set(
      filteredJobs?.map(job => job?.companyId || job?.companySlug)?.filter(Boolean)
    );
    
    const activeJobCompanyNames = new Set(
      filteredJobs?.map(job => job?.companyName)?.filter(Boolean)?.map(name => name?.toLowerCase())
    );

    filteredCompanies = filteredCompanies?.filter(company => {
      return activeJobCompanyIds?.has(company?.id) ||
        activeJobCompanyIds?.has(company?.slug) ||
        activeJobCompanyNames?.has(company?.name?.toLowerCase());
    });
  }

  // Add company results
  results = results?.concat(filteredCompanies?.map(company => ({ 
    type: 'company', 
    data: company,
    id: company?.id,
    name: company?.name,
    slug: company?.slug,
    industry: company?.industry,
    openRoles: company?.openRoles,
    locations: company?.locations
  })));

  // Apply filters to locations - ENHANCED: Only include locations that host active jobs
  let filteredLocations = [...locations];
  
  if (filters?.query?.trim()) {
    const queryLower = filters?.query?.toLowerCase();
    
    // Get location IDs and company info from active jobs only
    const activeJobLocationIds = new Set();
    const activeJobCompanyInfo = new Set();
    
    filteredJobs?.forEach(job => {
      job?.locations?.forEach(loc => {
        if (loc?.locationId) activeJobLocationIds?.add(loc?.locationId);
        if (loc?.city) activeJobCompanyInfo?.add(loc?.city?.toLowerCase());
      });
      if (job?.companyId) activeJobCompanyInfo?.add(job?.companyId);
      if (job?.companySlug) activeJobCompanyInfo?.add(job?.companySlug);
      if (job?.companyName) activeJobCompanyInfo?.add(job?.companyName?.toLowerCase());
    });

    filteredLocations = filteredLocations?.filter(location => {
      // Direct location match
      const directMatch = location?.city?.toLowerCase()?.includes(queryLower) ||
        location?.region?.toLowerCase()?.includes(queryLower) ||
        location?.country?.toLowerCase()?.includes(queryLower) ||
        location?.companyName?.toLowerCase()?.includes(queryLower) ||
        location?.addressLine1?.toLowerCase()?.includes(queryLower);

      // Location hosts active jobs
      const hasActiveJobs = activeJobLocationIds?.has(location?.id) ||
        activeJobCompanyInfo?.has(location?.companyId) ||
        activeJobCompanyInfo?.has(location?.companySlug) ||
        activeJobCompanyInfo?.has(location?.companyName?.toLowerCase()) ||
        activeJobCompanyInfo?.has(location?.city?.toLowerCase());

      return directMatch || hasActiveJobs;
    });
  } else {
    // CRITICAL FIX: Even without a query, only show locations that host active jobs
    const activeJobLocationIds = new Set();
    const activeJobCompanyInfo = new Set();
    
    filteredJobs?.forEach(job => {
      job?.locations?.forEach(loc => {
        if (loc?.locationId) activeJobLocationIds?.add(loc?.locationId);
        if (loc?.city) activeJobCompanyInfo?.add(loc?.city?.toLowerCase());
      });
      if (job?.companyId) activeJobCompanyInfo?.add(job?.companyId);
      if (job?.companySlug) activeJobCompanyInfo?.add(job?.companySlug);
      if (job?.companyName) activeJobCompanyInfo?.add(job?.companyName?.toLowerCase());
    });

    filteredLocations = filteredLocations?.filter(location => {
      return activeJobLocationIds?.has(location?.id) ||
        activeJobCompanyInfo?.has(location?.companyId) ||
        activeJobCompanyInfo?.has(location?.companySlug) ||
        activeJobCompanyInfo?.has(location?.companyName?.toLowerCase()) ||
        activeJobCompanyInfo?.has(location?.city?.toLowerCase());
    });
  }

  if (filters?.location?.city || filters?.location?.region || filters?.location?.country) {
    filteredLocations = filteredLocations?.filter(location => {
      const cityMatch = !filters?.location?.city || 
        location?.city?.toLowerCase()?.includes(filters?.location?.city?.toLowerCase());
      const regionMatch = !filters?.location?.region || 
        location?.region?.toLowerCase()?.includes(filters?.location?.region?.toLowerCase());
      const countryMatch = !filters?.location?.country || 
        location?.country?.toLowerCase()?.includes(filters?.location?.country?.toLowerCase());
      
      return cityMatch && regionMatch && countryMatch;
    });
  }

  // Add location results
  results = results?.concat(filteredLocations?.map(location => ({ 
    type: 'location', 
    data: location,
    id: location?.id,
    city: location?.city,
    region: location?.region,
    companyName: location?.companyName,
    totalRoles: location?.totalRoles,
    activeRoles: location?.activeRoles
  })));

  // Sort results: jobs first, then companies, then locations
  results?.sort((a, b) => {
    const order = { 'job': 0, 'company': 1, 'location': 2 };
    return order?.[a?.type] - order?.[b?.type];
  });

  return results;
};